/**
 * Acceso a la landing `/invitacion-premium` (y aliases).
 *
 * Política (admin / super-admin vía /admin/cta-por-region → wishlist_geo_config):
 * - **Fuera de Argentina** (edge country ≠ AR): siempre permitida.
 * - **Argentina**: solo si el CTA resuelto para esa locación es `community`
 *   (`showCommunity=true` en Worker / `/api/cta-flags`).
 * - **Token one-time** (`?ot=`): bypass geo tras consume exitoso (Instagram DM).
 *   El token se quema en POST `/api/premium-invite-share/consume` al tocar
 *   "Abrir invitación" (no en GET, para no invalidar por crawlers de IG).
 *
 * No se usa el override client `?country=AR` como autoridad: un tester en US
 * con query param no debe quedar bloqueado; un visitante real AR sin community
 * sí debe redirigirse a home (salvo token share válido en sesión).
 *
 * Success (`/invitacion-premium/success`) no se gatea acá (ruta aparte).
 */

import { PREMIUM_INVITE_LANDING_PATH } from "@/lib/premiumVipCheckoutDestination";
import { normalizeCountryCode } from "@/lib/siteGeoPolicy";

/** Query param for one-time Instagram share links. */
export const PREMIUM_INVITE_SHARE_QUERY = "ot";

/** sessionStorage key after successful burn (same tab / IG browser session). */
export const PREMIUM_INVITE_SHARE_SESSION_KEY = "premium_invite_share_granted";

/** Paths de landing (no success) que deben respetar el gate geo AR. */
const LANDING_EXACT = new Set([
  PREMIUM_INVITE_LANDING_PATH,
  "/premium-invite",
  "/invitacion",
]);

/**
 * True si el pathname es la landing de invitación premium (no success).
 * Acepta trailing slash.
 */
export function isPremiumInviteAccessGatedPath(pathname: string): boolean {
  const raw = String(pathname || "").split("?")[0] || "/";
  const p = raw.replace(/\/+$/, "") || "/";
  if (p === `${PREMIUM_INVITE_LANDING_PATH}/success`) return false;
  if (p.startsWith(`${PREMIUM_INVITE_LANDING_PATH}/`)) {
    return true;
  }
  return LANDING_EXACT.has(p);
}

/**
 * ¿Puede este visitante ver la landing?
 *
 * @param edgeCountry País del edge/Worker (`__TEMPLATE_CONFIG__.country`), no el
 *   override SPA de QA.
 * @param showCommunity Flag geo del Worker o de `/api/cta-flags` fresco.
 * @param shareTokenGranted Token one-time consumido en esta sesión (Instagram).
 */
export function isPremiumInviteLandingAllowed(opts: {
  edgeCountry: string | null | undefined;
  showCommunity?: boolean | null;
  shareTokenGranted?: boolean | null;
}): boolean {
  if (opts.shareTokenGranted === true) return true;
  const cc = normalizeCountryCode(opts.edgeCountry);
  if (cc !== "AR") return true;
  return opts.showCommunity === true;
}

/** Destino al bloquear: home (mismo origin, sin query de la invite). */
export const PREMIUM_INVITE_BLOCKED_REDIRECT = "/";

/** Read `ot` (or legacy `share`) from the current URL. */
export function readPremiumInviteShareTokenFromSearch(
  search?: string,
): string | null {
  if (typeof window === "undefined" && search == null) return null;
  const q = search ?? (typeof window !== "undefined" ? window.location.search : "");
  const params = new URLSearchParams(q.startsWith("?") ? q : `?${q}`);
  const ot =
    params.get(PREMIUM_INVITE_SHARE_QUERY)?.trim() ||
    params.get("share")?.trim() ||
    "";
  return ot.length >= 8 ? ot : null;
}

/**
 * Session grant after a successful burn.
 * - With URL token: only if it matches the burned token (new ot ≠ old grant).
 * - Without URL token (post-strip refresh): any stored grant rehydrates this tab
 *   so AR Instagram users keep access after history.replaceState removed ?ot=.
 */
export function readShareGrantedFromSession(
  token?: string | null,
): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(PREMIUM_INVITE_SHARE_SESSION_KEY);
    if (!raw || raw.length < 8) return false;
    if (token && token.length >= 8) return raw === token;
    // No ot in URL — rehydrate grant for this browser session.
    return true;
  } catch {
    return false;
  }
}

/** True if this tab already opened a one-time invite (any token). */
export function hasShareGrantInSession(): boolean {
  return readShareGrantedFromSession(null);
}

export function writeShareGrantedToSession(token: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(PREMIUM_INVITE_SHARE_SESSION_KEY, token);
  } catch {
    /* private mode */
  }
}

export function clearShareGrantedSession(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(PREMIUM_INVITE_SHARE_SESSION_KEY);
  } catch {
    /* ignore */
  }
}
